// File: frontend/src/components/search/SearchBar.tsx
// src/components/search/SearchBar.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MdSchedule,
  MdCancel,
  MdWarning,
} from "react-icons/md";
import {
  QuickSearchResult,
  SearchMatchResult,
} from "../../models/SearchModel";
import { quickSearch } from "../../services/searchService";
import { generateMatchSlug, generateProfileUrl } from "../../utils/slugGenerator";
import { entryPermalinkPath } from "../../utils/canonicalUrl";
import { entryTopicLabel } from "../../utils/searchUtils";
import { resolveClubName } from "../../services/matchService";
// Search thresholds are now read from SiteSettingsContext (admin-configurable)
// instead of hardcoded constants, so admin panel changes take effect immediately.
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { InputClearButton } from "../common/InputClearButton";
import { usePersonRoles } from "../../hooks/usePersonRoles";
import "./SearchBar.css";

// ─── helpers ─────────────────────────────────────────────────────────────────

const isEmpty = (r: QuickSearchResult): boolean =>
  r.matches.length === 0 &&
  r.teams.length === 0 &&
  r.entries.length === 0 &&
  r.authors.length === 0 &&
  r.leagues.length === 0 &&
  r.people.length === 0 &&
  (r.clubs ?? []).length === 0 &&
  (r.customTopics ?? []).length === 0;

const matchSlugFromResult = (m: SearchMatchResult): string =>
  generateMatchSlug({
    id: m.id,
    homeTeam: m.homeTeam,
    awayTeam: m.awayTeam,
    matchDate: new Date(m.matchDate + "T00:00:00"),
  } as any);

// ─── DropdownMatchRow — renders "HomeTeam [score/vs] AwayTeam [icon]" inline ─

interface DropdownMatchRowProps {
  m: SearchMatchResult;
}

const DropdownMatchRow: React.FC<DropdownMatchRowProps> = ({ m }) => {
  const { t } = useTranslation();
  const status = m.status ?? "COMPLETED";

  const homeName = m.homeTeamTr || m.homeTeam;
  const awayName = m.awayTeamTr || m.awayTeam;

  // Status icon/indicator rendered AFTER away team name
  const renderStatusIcon = () => {
    if (status === "LIVE") {
      // Pulsing red dot after away team name
      return <span className="search-live-dot" aria-hidden="true" />;
    }
    if (status === "POSTPONED") {
      return (
        <span
          className="search-dropdown-status-icon search-dropdown-status-icon--postponed"
          title={t("match_status_postponed")}
        >
          <MdSchedule />
        </span>
      );
    }
    if (status === "CANCELLED") {
      return (
        <span
          className="search-dropdown-status-icon search-dropdown-status-icon--cancelled"
          title={t("match_status_cancelled")}
        >
          <MdCancel />
        </span>
      );
    }
    if (status === "ABANDONED") {
      return (
        <span
          className="search-dropdown-status-icon search-dropdown-status-icon--abandoned"
          title={t("match_status_abandoned")}
        >
          <MdWarning />
        </span>
      );
    }
    return null;
  };

  // Middle part between team names — live dot is NOT here, it goes after away team
  const renderMiddle = () => {
    if (status === "COMPLETED" || status === "ABANDONED") {
      return <span className="search-dropdown-score">{m.homeScore}–{m.awayScore}</span>;
    }
    if (status === "LIVE") {
      return <span className="search-dropdown-score search-dropdown-score--live">{m.homeScore}–{m.awayScore}</span>;
    }
    // SCHEDULED, POSTPONED, CANCELLED — no score
    return <span className="search-dropdown-vs">vs</span>;
  };

  return (
    <span className="search-item-primary search-dropdown-match-row">
      <span>{homeName}</span>
      {renderMiddle()}
      <span>{awayName}</span>
      {renderStatusIcon()}
    </span>
  );
};

// ─── component ────────────────────────────────────────────────────────────────

export const SearchBar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // md_person_role is the source of truth for role labels — the static
  // person_role_* keys only cover the roles that once lived in the Java enum,
  // so admin-defined roles (e.g. PUNDIT) rendered as raw keys. Same resolution
  // order as PersonRow: master-data name → legacy key → raw code.
  const { translateRole } = usePersonRoles();
  const roleLabel = (code: string): string => {
    const mdName = translateRole(code);
    if (mdName && mdName !== code) return mdName;
    return t(`person_role_${code.toLowerCase()}`, { defaultValue: code });
  };

  // Read live search thresholds from the backend (via SiteSettingsContext)
  const { searchMinChars, searchDelayMs } = useSiteSettings();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<QuickSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);

  // Advanced options state
  const [advAuthor, setAdvAuthor] = useState("");
  const [advDateFrom, setAdvDateFrom] = useState("");
  const [advDateTo, setAdvDateTo] = useState("");

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionsOpenRef = useRef(optionsOpen);
  optionsOpenRef.current = optionsOpen;

  // Close everything when clicking outside the wrapper
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setOptionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keep the search box in sync with the URL — the URL is the single source
  // of truth for the search state:
  //   - On the search results page ("/?q=...&yazar=...&baslangic=...&bitis=...")
  //     the keyword and the advanced filters mirror the URL, so a shared link,
  //     a page reload or the browser back button shows the matching terms.
  //   - On every other page the box is emptied. Previously the last search
  //     stayed in the box after navigating to a club, a topic, etc., which
  //     looked as if that page were a search result.
  // Runs only when the address changes, never while the user is typing.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const onSearchPage =
      location.pathname === "/" &&
      (params.has("q") || params.has("yazar") || params.has("baslangic") || params.has("bitis"));

    // Drop any pending typeahead request and close both panels
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setResults(null);
    setDropdownOpen(false);
    setOptionsOpen(false);

    if (onSearchPage) {
      setQuery(params.get("q") ?? "");
      setAdvAuthor(params.get("yazar") ?? "");
      setAdvDateFrom(params.get("baslangic") ?? "");
      setAdvDateTo(params.get("bitis") ?? "");
    } else {
      setQuery("");
      setAdvAuthor("");
      setAdvDateFrom("");
      setAdvDateTo("");
    }
  }, [location.pathname, location.search]);

  // Debounced typeahead — fires after searchDelayMs ms (from admin settings)
  const handleInput = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Use admin-configured min chars instead of a hardcoded constant
    if (value.trim().length < searchMinChars) {
      setResults(null);
      setDropdownOpen(false);
      return;
    }

    // Use admin-configured debounce delay instead of a hardcoded constant
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await quickSearch(value.trim());
        setResults(data);
        // Only open typeahead when options panel is closed
        if (!optionsOpenRef.current) setDropdownOpen(true);
      } catch {
        // Silently ignore typeahead errors
      } finally {
        setLoading(false);
      }
    }, searchDelayMs);
  }, [searchMinChars, searchDelayMs]);

  // Full search — navigates to /?q=...
  // Filter-only searches (author and/or date range without a keyword) are allowed;
  // the submit is only a no-op when neither a keyword nor any filter is set.
  const submitSearch = useCallback(() => {
    const q = query.trim();
    // Strip an optional leading '@' — the placeholder shows "@yazar",
    // so users often type the username with it
    const author = advAuthor.trim().replace(/^@/, "");
    if (!q && !author && !advDateFrom && !advDateTo) return;
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (author) params.set("yazar", author);
    if (advDateFrom) params.set("baslangic", advDateFrom);
    if (advDateTo) params.set("bitis", advDateTo);
    setDropdownOpen(false);
    setOptionsOpen(false);
    navigate(`/?${params.toString()}`);
  }, [query, advAuthor, advDateFrom, advDateTo, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitSearch();
    if (e.key === "Escape") { setDropdownOpen(false); setOptionsOpen(false); }
  };

  // Toggle advanced panel; close typeahead while options are open
  const toggleOptions = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOptionsOpen((prev) => {
      if (!prev) setDropdownOpen(false);
      return !prev;
    });
  };

  const clearAdvanced = () => {
    setAdvAuthor("");
    setAdvDateFrom("");
    setAdvDateTo("");
  };

  const go = (path: string) => {
    setDropdownOpen(false);
    navigate(path);
  };

  const hasAdvancedValues = advAuthor || advDateFrom || advDateTo;

  return (
    <div className="search-bar-wrapper" ref={wrapperRef}>

      {/* ── Input row: [ text input ] [ search btn ] [ chevron btn ] ── */}
      <div className={`search-bar-input-row${optionsOpen ? " options-active" : ""}`}>
        <input
          ref={inputRef}
          className="search-bar-input"
          type="text"
          value={query}
          placeholder={t("search_placeholder")}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results && !isEmpty(results) && !optionsOpen) setDropdownOpen(true);
          }}
          autoComplete="off"
          spellCheck={false}
        />

        {/* Clears the keyword and its typeahead results — the advanced filters
            keep their own "temizle" button below */}
        {query !== "" && (
          <InputClearButton
            variant="inline"
            onClear={() => {
              setQuery("");
              setResults(null);
              setDropdownOpen(false);
              inputRef.current?.focus();
            }}
          />
        )}

        {/* Magnifier — submit search */}
        <button
          className="search-bar-btn search-bar-btn--submit"
          onClick={submitSearch}
          title={t("search_submit")}
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </button>

        {/* Chevron — opens advanced options panel */}
        <button
          className={`search-bar-btn search-bar-btn--chevron${hasAdvancedValues ? " has-values" : ""}${optionsOpen ? " open" : ""}`}
          onClick={toggleOptions}
          title={t("search_options")}
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </button>
      </div>

      {/* ── Advanced options panel ── */}
      {optionsOpen && (
        <div className="search-options-panel">
          {/* Panel title — the advanced search filters ENTRIES only,
              which was not obvious without an explicit label */}
          <div className="search-options-header">
            <span className="search-options-title">{t("search_options_title")}</span>
          </div>
          <div className="search-options-row">
            <label className="search-options-label">{t("search_option_author")}</label>
            <input
              className="search-options-input"
              type="text"
              value={advAuthor}
              placeholder="@yazar"
              onChange={(e) => setAdvAuthor(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {advAuthor !== "" && (
              <InputClearButton variant="inline" onClear={() => setAdvAuthor("")} />
            )}
          </div>
          <div className="search-options-row">
            <label className="search-options-label">{t("search_option_date")}</label>
            <div className="search-options-date-pair">
              <input
                className="search-options-input search-options-date"
                type="date"
                value={advDateFrom}
                onChange={(e) => setAdvDateFrom(e.target.value)}
              />
              <input
                className="search-options-input search-options-date"
                type="date"
                value={advDateTo}
                onChange={(e) => setAdvDateTo(e.target.value)}
              />
            </div>
          </div>
          <div className="search-options-actions">
            <button className="search-options-search-btn" onClick={submitSearch} type="button">
              {t("search_options_submit")}
            </button>
            {hasAdvancedValues && (
              <button className="search-options-clear-btn" onClick={clearAdvanced} type="button">
                {t("search_options_clear")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Typeahead dropdown ── */}
      {dropdownOpen && results && !isEmpty(results) && (
        <div className="search-dropdown">

          {/* Group order: Gündem → Kişi → Kulüp → Takım → Maç → Yazar → Tanım.
              Stadiums were removed from search entirely. */}

          {/* Custom topics ("Gündem") */}
          {(results.customTopics ?? []).length > 0 && (
            <div className="search-dropdown-group">
              <div className="search-dropdown-group-label">{t("search_category_custom_topics")}</div>
              {(results.customTopics ?? []).map((ct) => (
                <button
                  key={ct.id}
                  className="search-dropdown-item"
                  onClick={() => go(`/gundem/${ct.slug}`)}
                  type="button"
                >
                  <span className="search-item-primary">{ct.title}</span>
                  {ct.categoryName && (
                    <span className="search-item-meta">{ct.categoryName}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* People */}
          {results.people.length > 0 && (
            <div className="search-dropdown-group">
              <div className="search-dropdown-group-label">{t("search_category_people")}</div>
              {results.people.map((p) => (
                <button
                  key={p.id}
                  className="search-dropdown-item"
                  onClick={() => go(`/kisi/${p.slug}`)}
                  type="button"
                >
                  <span className="search-item-primary">
                    {p.knownName ?? `${p.firstName} ${p.lastName}`}
                  </span>
                  <span className="search-item-meta">
                    {p.latestRole ? roleLabel(p.latestRole) : ""}
                    {p.latestClub ? ` · ${p.latestClub}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Clubs */}
          {(results.clubs ?? []).length > 0 && (
            <div className="search-dropdown-group">
              <div className="search-dropdown-group-label">{t("search_category_clubs")}</div>
              {(results.clubs ?? []).map((c) => (
                <button
                  key={c.clubId}
                  className="search-dropdown-item"
                  onClick={() => go(`/kulup/${c.slug}`)}
                  type="button"
                >
                  <span className="search-item-primary">{resolveClubName(c)}</span>
                  <span className="search-item-meta">{c.shortName}</span>
                </button>
              ))}
            </div>
          )}

          {results.teams.length > 0 && (
            <div className="search-dropdown-group">
              <div className="search-dropdown-group-label">{t("search_category_teams")}</div>
              {results.teams.map((tm) => {
                const SPORT_SLUG: Record<string, string> = {
                  FOOTBALL: "futbol", BASKETBALL: "basketbol", VOLLEYBALL: "voleybol",
                  AMERICAN_FOOTBALL: "amerikan-futbol", BASEBALL: "beyzbol",
                  ICE_HOCKEY: "buz-hokeyi", HANDBALL: "hentbol",
                };
                const sportPart    = tm.sportType ? (SPORT_SLUG[tm.sportType] ?? tm.sportType.toLowerCase()) : "futbol";
                const genderSuffix = tm.gender === "FEMALE" ? "-kadin" : tm.gender === "MIXED" ? "-karma" : "";
                const codeSuffix   = tm.teamCode ? `-${tm.teamCode}` : "";
                const teamUrlSlug  = `${sportPart}${genderSuffix}${codeSuffix}`;
                return (
                  <button key={tm.teamId} className="search-dropdown-item" onClick={() => go(`/takim/${tm.clubSlug}/${teamUrlSlug}`)} type="button">
                    <span className="search-item-primary">{tm.displayName || tm.nameTr || tm.name}</span>
                    <span className="search-item-meta">
                      {tm.sportType ? t(`sport_type_${tm.sportType.toLowerCase()}`) : ""}
                      {tm.gender === "FEMALE" ? ` · ${t("match_gender_female")}` : tm.gender === "MALE" ? ` · ${t("match_gender_male")}` : ""}
                      {tm.teamCode ? ` · ${tm.teamCode.toUpperCase()}` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {results.matches.length > 0 && (
            <div className="search-dropdown-group">
              <div className="search-dropdown-group-label">{t("search_category_matches")}</div>
              {results.matches.map((m) => (
                <button key={m.id} className="search-dropdown-item" onClick={() => go(`/${matchSlugFromResult(m)}`)} type="button">
                  <DropdownMatchRow m={m} />
                  <span className="search-item-meta">
                    {new Date(m.matchDate + "T00:00:00").toLocaleDateString("tr-TR")}
                    {m.league ? ` · ${m.league}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}

          {results.authors.length > 0 && (
            <div className="search-dropdown-group">
              <div className="search-dropdown-group-label">{t("search_category_authors")}</div>
              {results.authors.map((au) => (
                <button key={au.userId} className="search-dropdown-item" onClick={() => go(generateProfileUrl(au.username, au.userId))} type="button">
                  <span className="search-item-primary">@{au.username}</span>
                  <span className="search-item-meta">{au.entryCount} {t("search_author_entry_count")}</span>
                </button>
              ))}
            </div>
          )}

          {/* Entries stay at the bottom of the dropdown */}
          {results.entries.length > 0 && (
            <div className="search-dropdown-group">
              <div className="search-dropdown-group-label">{t("search_category_entries")}</div>
              {results.entries.map((en) => (
                <button
                  key={en.entryId}
                  className="search-dropdown-item"
                  onClick={() => {
                    // The permalink route resolves the topic AND the page the
                    // entry sits on. Building a topic URL here could only ever
                    // produce page 1, where the anchor may not exist.
                    go(entryPermalinkPath(en.entryId));
                  }}
                  type="button"
                >
                  <span className="search-item-primary search-item-snippet">{en.snippet}</span>
                  <span className="search-item-meta">
                    <span className="search-item-meta-author">@{en.authorUsername}</span>
                    {entryTopicLabel(en) && (
                      <span className="search-item-meta-topic"> · {entryTopicLabel(en)}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Leagues group removed: there is no dedicated league/cup page to link to,
              so these results were showing up as effectively unclickable. */}

          <button className="search-dropdown-show-all" onClick={submitSearch} type="button">
            {t("search_show_all")} "{query}" →
          </button>
        </div>
      )}

      {/* Empty state */}
      {dropdownOpen && results && isEmpty(results) && !loading && (
        <div className="search-dropdown">
          <div className="search-dropdown-empty">{t("search_no_results")}</div>
        </div>
      )}

      {/* Loading dots */}
      {loading && (
        <div className="search-bar-loading">
          <div className="search-bar-loading-dot" />
          <div className="search-bar-loading-dot" />
          <div className="search-bar-loading-dot" />
        </div>
      )}
    </div>
  );
};